import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Loader2, Camera, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import UsageLimitBanner from '../components/UsageLimitBanner';
import LimitReachedModal from '../components/LimitReachedModal';
import CancelledSubscriptionBanner from '../components/CancelledSubscriptionBanner';
import { useSubscription } from '../hooks/useSubscription';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateRecipePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canCreateRecipe, refresh } = useSubscription();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [scanningImage, setScanningImage] = useState(false); // NOVO: estado de scan
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    servings: 4,
    prep_time: 0,
    cook_time: 0,
    ingredients: [{ name: '', quantity: '', unit: '' }],
    instructions: ['']
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
      if (response.data.categories.length > 0) {
        setFormData(prev => ({ ...prev, category: response.data.categories[0] }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index][field] = value;
    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', quantity: '', unit: '' }]
    }));
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length > 1) {
      setFormData(prev => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index)
      }));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData(prev => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeInstruction = (index) => {
    if (formData.instructions.length > 1) {
      setFormData(prev => ({
        ...prev,
        instructions: prev.instructions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CHECK SUBSCRIPTION LIMIT FIRST
    if (!canCreateRecipe) {
      setShowLimitModal(true);
      return;
    }
    
    // Validation
    if (!formData.title.trim()) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }
    if (!formData.description.trim()) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }
    if (formData.ingredients.some(ing => !ing.name.trim() || !ing.quantity.trim())) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }
    if (formData.instructions.some(inst => !inst.trim())) {
      toast.error(t('recipe.errorSavingRecipe'));
      return;
    }

    try {
      setLoading(true);
      toast.info(t('recipe.creatingRecipe'));
      
      const response = await axios.post(`${API}/recipes`, formData);
      
      toast.success(t('recipe.recipeSaved'));
      await refresh(); // Refresh usage stats
      navigate(`/recipe/${response.data.id}`);
    } catch (error) {
      console.error('Error creating recipe:', error);
      toast.error(t('recipe.errorSavingRecipe'));
    } finally {
      setLoading(false);
    }
  };

  const handleScanRecipe = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setScanningImage(true);
      toast.info('📸 ' + t('recipe.scanningImage'));
      
      // Converter para base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        try {
          // Chamar API para extrair receita da imagem
          const response = await axios.post(`${API}/ai/scan-recipe-paper`, {
            image: base64
          }, { withCredentials: true });
          
          const extracted = response.data;
          
          // Preencher formulário com dados extraídos
          setFormData({
            title: extracted.title || '',
            description: extracted.description || '',
            category: extracted.category || categories[0] || '',
            servings: extracted.servings || 4,
            prep_time: extracted.prep_time || 0,
            cook_time: extracted.cook_time || 0,
            ingredients: extracted.ingredients?.length > 0 
              ? extracted.ingredients 
              : [{ name: '', quantity: '', unit: '' }],
            instructions: extracted.instructions?.length > 0
              ? extracted.instructions
              : ['']
          });
          
          toast.success('✅ ' + t('recipe.scanSuccess'));
        } catch (error) {
          console.error('Scan error:', error);
          toast.error(t('recipe.scanError'));
        } finally {
          setScanningImage(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setScanningImage(false);
      toast.error(t('recipe.scanError'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            data-testid="back-button"
            onClick={() => navigate('/')} 
            variant="ghost"
            disabled={loading}
            className="hover:bg-orange-50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('common.back')}
          </Button>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Cancelled Subscription Banner */}
        <CancelledSubscriptionBanner className="mb-4" />
        
        {/* Usage Limit Banner */}
        <UsageLimitBanner className="mb-8" />
        
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-2">{t('recipe.newRecipe')}</h1>
              <p className="text-lg text-gray-600">{t('recipe.aiWillGenerate')}</p>
            </div>
            
            {/* Botões de Digitalização */}
            <div className="flex flex-col gap-2">
              {/* Input para CÂMERA (tirar foto) - iOS e Android */}
              <input
                type="file"
                accept="image/*"
                capture="camera"
                onChange={handleScanRecipe}
                className="hidden"
                id="camera-input"
              />
              
              {/* Input para UPLOAD (galeria) */}
              <input
                type="file"
                accept="image/*"
                onChange={handleScanRecipe}
                className="hidden"
                id="upload-input"
              />
              
              <div className="flex gap-2">
                {/* Botão Tirar Foto */}
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    const input = document.getElementById('camera-input');
                    input.value = ''; // Reset para permitir mesma foto
                    input.click();
                  }}
                  disabled={scanningImage}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex-1"
                >
                  {scanningImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('recipe.readingImage')}
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      📸 {t('recipe.camera')}
                    </>
                  )}
                </Button>
                
                {/* Botão Upload */}
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    const input = document.getElementById('upload-input');
                    input.value = '';
                    input.click();
                  }}
                  disabled={scanningImage}
                  variant="outline"
                  className="border-purple-500 text-purple-600 hover:bg-purple-50 flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  📁 {t('recipe.gallery')}
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                {t('recipe.aiReadsRecipes')}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="p-6 border-2 border-amber-100">
            <h2 className="text-2xl font-semibold text-amber-900 mb-6">{t('recipe.basicInfo')}</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-base font-medium">{t('recipe.recipeTitle')}</Label>
                <Input
                  data-testid="recipe-title-input"
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={t('recipe.recipeTitlePlaceholder')}
                  className="mt-2 text-base"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-base font-medium">{t('recipe.description')}</Label>
                <Textarea
                  data-testid="recipe-description-input"
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('recipe.descriptionPlaceholder')}
                  className="mt-2 min-h-24 text-base"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="category" className="text-base font-medium">{t('recipe.category')}</Label>
                  <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                    <SelectTrigger data-testid="category-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="servings" className="text-base font-medium">{t('recipe.servings')}</Label>
                  <Input
                    data-testid="servings-input"
                    id="servings"
                    type="number"
                    min="1"
                    value={formData.servings}
                    onChange={(e) => handleInputChange('servings', parseInt(e.target.value) || 1)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="prep_time" className="text-base font-medium">{t('recipe.prepTime')} (min)</Label>
                  <Input
                    data-testid="prep-time-input"
                    id="prep_time"
                    type="number"
                    min="0"
                    value={formData.prep_time + formData.cook_time}
                    onChange={(e) => handleInputChange('prep_time', parseInt(e.target.value) || 0)}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Ingredients */}
          <Card className="p-6 border-2 border-amber-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-amber-900">{t('recipe.ingredientsCard')}</h2>
              <Button 
                data-testid="add-ingredient-button"
                type="button"
                onClick={addIngredient}
                variant="outline"
                size="sm"
                className="border-orange-300 hover:bg-orange-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('recipe.addIngredient')}
              </Button>
            </div>

            <div className="space-y-3">
              {formData.ingredients.map((ingredient, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    data-testid={`ingredient-name-${index}`}
                    type="text"
                    placeholder={t('recipe.ingredientNamePlaceholder')}
                    value={ingredient.name}
                    onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    data-testid={`ingredient-quantity-${index}`}
                    type="text"
                    placeholder={t('recipe.quantityPlaceholder')}
                    value={ingredient.quantity}
                    onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                    className="w-24"
                  />
                  <Input
                    data-testid={`ingredient-unit-${index}`}
                    type="text"
                    placeholder={t('recipe.unitPlaceholder')}
                    value={ingredient.unit}
                    onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                    className="w-32"
                  />
                  {formData.ingredients.length > 1 && (
                    <Button
                      data-testid={`remove-ingredient-${index}`}
                      type="button"
                      onClick={() => removeIngredient(index)}
                      variant="outline"
                      size="icon"
                      className="border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Instructions */}
          <Card className="p-6 border-2 border-amber-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-amber-900">{t('recipe.instructionsCard')}</h2>
              <Button 
                data-testid="add-instruction-button"
                type="button"
                onClick={addInstruction}
                variant="outline"
                size="sm"
                className="border-orange-300 hover:bg-orange-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('recipe.addStep')}
              </Button>
            </div>

            <div className="space-y-3">
              {formData.instructions.map((instruction, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm mt-2">
                    {index + 1}
                  </div>
                  <Textarea
                    data-testid={`instruction-${index}`}
                    value={instruction}
                    onChange={(e) => handleInstructionChange(index, e.target.value)}
                    placeholder={t('recipe.instructionPlaceholder')}
                    className="flex-1 min-h-20"
                  />
                  {formData.instructions.length > 1 && (
                    <Button
                      data-testid={`remove-instruction-${index}`}
                      type="button"
                      onClick={() => removeInstruction(index)}
                      variant="outline"
                      size="icon"
                      className="border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button"
              onClick={() => navigate('/')}
              variant="outline"
              disabled={loading}
              className="px-8"
            >
              {t('common.cancel')}
            </Button>
            <Button 
              data-testid="submit-recipe-button"
              type="submit"
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700 text-white px-8"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('recipe.creatingRecipe')}
                </>
              ) : (
                t('recipe.createRecipe')
              )}
            </Button>
          </div>
        </form>
      </div>
      
      {/* Limit Reached Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        type="recipe"
      />
    </div>
  );
};

export default CreateRecipePage;